import Products from 'merchandise-lib/constants/Product';
// HSoC requires that all owned seats be at the same tier. So if a portal owns both Core and Front Office Seats, both seat types must be at the same tier.
// Therefore, it is safe to determine the owned tier by checking the tier of the first HSoC Seat apiName found in the ownedApiNames array.
const HSOC_API_NAME_TO_TIER_MAP = {
  [Products.CORE_SEATS_STARTER_HSOC]: 'starter',
  [Products.CORE_SEATS_PROFESSIONAL_HSOC]: 'professional',
  [Products.FRONT_OFFICE_SEATS_PROFESSIONAL]: 'professional',
  [Products.CORE_SEATS_ENTERPRISE_HSOC]: 'enterprise',
  [Products.FRONT_OFFICE_SEATS_ENTERPRISE]: 'enterprise'
};
export const getOwnedHsocTier = ownedHSoCApiNames => {
  for (const apiName of ownedHSoCApiNames) {
    const tier = HSOC_API_NAME_TO_TIER_MAP[apiName];
    if (tier) {
      return tier;
    }
  }
  return undefined;
};
const applyOwnedHSoCApiNameTierAdjustment = (mappedHSoCUpgradePointApiName, ownedTier) => {
  if (!ownedTier) return mappedHSoCUpgradePointApiName;
  if (mappedHSoCUpgradePointApiName === Products.CORE_SEATS_STARTER_HSOC) {
    if (ownedTier === 'starter') {
      return Products.CORE_SEATS_STARTER_HSOC;
    } else if (ownedTier === 'professional') {
      return Products.CORE_SEATS_PROFESSIONAL_HSOC;
    } else if (ownedTier === 'enterprise') {
      return Products.CORE_SEATS_ENTERPRISE_HSOC;
    }
  } else if (mappedHSoCUpgradePointApiName === Products.CORE_SEATS_PROFESSIONAL_HSOC) {
    if (ownedTier === 'starter') {
      return Products.CORE_SEATS_PROFESSIONAL_HSOC;
    } else if (ownedTier === 'professional') {
      return Products.CORE_SEATS_PROFESSIONAL_HSOC;
    } else if (ownedTier === 'enterprise') {
      return Products.CORE_SEATS_ENTERPRISE_HSOC;
    }
  } else if (mappedHSoCUpgradePointApiName === Products.CORE_SEATS_ENTERPRISE_HSOC) {
    if (ownedTier === 'starter') {
      return Products.CORE_SEATS_ENTERPRISE_HSOC;
    } else if (ownedTier === 'professional') {
      return Products.CORE_SEATS_ENTERPRISE_HSOC;
    } else if (ownedTier === 'enterprise') {
      return Products.CORE_SEATS_ENTERPRISE_HSOC;
    }
  } else if (mappedHSoCUpgradePointApiName === Products.FRONT_OFFICE_SEATS_PROFESSIONAL) {
    if (ownedTier === 'starter') {
      return Products.FRONT_OFFICE_SEATS_PROFESSIONAL;
    } else if (ownedTier === 'professional') {
      return Products.FRONT_OFFICE_SEATS_PROFESSIONAL;
    } else if (ownedTier === 'enterprise') {
      return Products.FRONT_OFFICE_SEATS_ENTERPRISE;
    }
  } else if (mappedHSoCUpgradePointApiName === Products.FRONT_OFFICE_SEATS_ENTERPRISE) {
    if (ownedTier === 'starter') {
      return Products.FRONT_OFFICE_SEATS_ENTERPRISE;
    } else if (ownedTier === 'professional') {
      return Products.FRONT_OFFICE_SEATS_ENTERPRISE;
    } else if (ownedTier === 'enterprise') {
      return Products.FRONT_OFFICE_SEATS_ENTERPRISE;
    }
  }
  return mappedHSoCUpgradePointApiName;
};

// These features are moving down from the Enterprise tier to the Professional tier for HSoC pilot users.
// We cannot update the feature configs to associate these features with the Professional tier because
// that would impact users outside of the pilot. So we need to include this additional mapping layer.
// https://github.com/HubSpotEngineering/growth-ebx-planning/issues/1660
export const FEATURE_TO_HSOC_PRODUCT_MAP = {
  activityLogging: Products.CORE_SEATS_PROFESSIONAL_HSOC,
  aiCallTranscriptEnrichmentBeta: Products.CORE_SEATS_PROFESSIONAL_HSOC,
  codeAlerts: Products.CORE_SEATS_PROFESSIONAL_HSOC,
  conditionalSLAs: Products.FRONT_OFFICE_SEATS_PROFESSIONAL,
  emailSendTimeOptimizationBeta: Products.CORE_SEATS_PROFESSIONAL_HSOC,
  customBehavioralEvents: Products.CORE_SEATS_PROFESSIONAL_HSOC,
  customObjects: Products.CORE_SEATS_PROFESSIONAL_HSOC,
  customerJourneyAnalytics: Products.CORE_SEATS_PROFESSIONAL_HSOC,
  dealJourneyAnalytics: Products.CORE_SEATS_PROFESSIONAL_HSOC,
  emailSendFrequencyCap: Products.CORE_SEATS_PROFESSIONAL_HSOC,
  eventVisualizer: Products.CORE_SEATS_PROFESSIONAL_HSOC,
  capacityLimitsForLiveChat: Products.FRONT_OFFICE_SEATS_PROFESSIONAL,
  ticketCapacityLimitsForUsers: Products.FRONT_OFFICE_SEATS_PROFESSIONAL,
  journeyAutomationForMarketers: Products.CORE_SEATS_PROFESSIONAL_HSOC,
  leadFormRouting: Products.FRONT_OFFICE_SEATS_PROFESSIONAL,
  lookalikeLists: Products.CORE_SEATS_PROFESSIONAL_HSOC,
  marketingEmailSingleSendAPI: Products.CORE_SEATS_PROFESSIONAL_HSOC,
  multiTouchRevenueAttribution: Products.CORE_SEATS_PROFESSIONAL_HSOC,
  salesforceCustomObjectSync: Products.CORE_SEATS_PROFESSIONAL_HSOC,
  webApps: Products.CORE_SEATS_PROFESSIONAL_HSOC
};
export const HUB_TO_HSOC_PRODUCT_MAP = {
  [Products.CRM_SUITE_STARTER_BUNDLE]: Products.CORE_SEATS_STARTER_HSOC,
  [Products.MARKETING_HUB_STARTER]: Products.CORE_SEATS_STARTER_HSOC,
  [Products.MARKETING_HUB_PROFESSIONAL]: Products.CORE_SEATS_PROFESSIONAL_HSOC,
  [Products.MARKETING_HUB_ENTERPRISE]: Products.CORE_SEATS_ENTERPRISE_HSOC,
  [Products.CMS_HUB_STARTER]: Products.CORE_SEATS_STARTER_HSOC,
  [Products.CMS_HUB_PROFESSIONAL]: Products.CORE_SEATS_PROFESSIONAL_HSOC,
  [Products.CMS_HUB_ENTERPRISE]: Products.CORE_SEATS_ENTERPRISE_HSOC,
  [Products.OPERATIONS_HUB_STARTER]: Products.CORE_SEATS_STARTER_HSOC,
  [Products.OPERATIONS_HUB_PROFESSIONAL]: Products.CORE_SEATS_PROFESSIONAL_HSOC,
  [Products.OPERATIONS_HUB_ENTERPRISE]: Products.CORE_SEATS_ENTERPRISE_HSOC,
  [Products.SALES_HUB_STARTER]: Products.CORE_SEATS_STARTER_HSOC,
  [Products.SALES_HUB_PROFESSIONAL]: Products.FRONT_OFFICE_SEATS_PROFESSIONAL,
  [Products.SALES_HUB_ENTERPRISE]: Products.FRONT_OFFICE_SEATS_ENTERPRISE,
  [Products.SERVICE_HUB_STARTER]: Products.CORE_SEATS_STARTER_HSOC,
  [Products.SERVICE_HUB_PROFESSIONAL]: Products.FRONT_OFFICE_SEATS_PROFESSIONAL,
  [Products.SERVICE_HUB_ENTERPRISE]: Products.FRONT_OFFICE_SEATS_ENTERPRISE,
  [Products.COMMERCE_HUB_PROFESSIONAL]: Products.FRONT_OFFICE_SEATS_PROFESSIONAL,
  [Products.COMMERCE_HUB_ENTERPRISE]: Products.FRONT_OFFICE_SEATS_ENTERPRISE
};

/**
 * Maps an upgrade configuration's product API name to its HubSpot On Credits (HSoC) equivalent.
 *
 * For portals in the HubSpot On Credits pilot program, traditional Hub products
 * (e.g., MARKETING_HUB_PROFESSIONAL) need to be mapped to their HSoC seat-based
 * equivalents (e.g., CORE_SEATS_PROFESSIONAL_HSOC).
 *
 * This transformation ensures that:
 * - Purchase flows direct users to the correct HSoC product
 * - Pricing and feature information reflect the HSoC model
 * - The upgrade experience is consistent with the pilot program requirements
 *
 * @param upgradeConfig - The upgrade configuration to transform
 * @returns The upgrade configuration with transformed bestFitApiNameForPurchase if a mapping exists,
 *          or the original configuration if no mapping is needed
 *
 * @example
 * const config = { bestFitApiNameForPurchase: 'MARKETING_HUB_PROFESSIONAL', ... };
 * const result = mapProductForHubSpotOnCredits(config, []);
 * // result.bestFitApiNameForPurchase === 'CORE_SEATS_PROFESSIONAL_HSOC'
 */
export const mapProductForHubSpotOnCredits = (upgradeConfig, ownedHSoCApiNames) => {
  const originalApiName = upgradeConfig.bestFitApiNameForPurchase;
  const featureMappedHSoCApiName = FEATURE_TO_HSOC_PRODUCT_MAP[upgradeConfig.featureStoreFeature];
  const hubMappedHSoCApiName = HUB_TO_HSOC_PRODUCT_MAP[originalApiName];
  const hubOrFeatureMappedHSoCApiName = featureMappedHSoCApiName !== null && featureMappedHSoCApiName !== void 0 ? featureMappedHSoCApiName : hubMappedHSoCApiName;
  if (!hubOrFeatureMappedHSoCApiName) {
    return upgradeConfig;
  }
  const ownedTier = getOwnedHsocTier(ownedHSoCApiNames);
  const finalApiName = applyOwnedHSoCApiNameTierAdjustment(hubOrFeatureMappedHSoCApiName, ownedTier);
  return Object.assign({}, upgradeConfig, {
    bestFitApiNameForPurchase: finalApiName
  });
};